import { useEffect, useRef, useMemo } from "react"
import type { Editor } from "@tiptap/react"
import { debounce } from "lodash"

interface UseTiptapEditorProps {
  editor: Editor | null
  isEditing: boolean
  content: string
  onUpdate?: (markdown: string) => void
  /** Called at once on every user edit, before the debounced `onUpdate`. */
  onUserEdit?: () => void
}

/**
 * Syncs the Tiptap editor with external content (Zustand store).
 * Replaces the old useTiptapSync which was coupled to GraphContext.
 */
export function useTiptapEditor({
  editor,
  isEditing,
  content,
  onUpdate,
  onUserEdit,
}: UseTiptapEditorProps) {
  const isInternalUpdate = useRef(false)
  const lastSyncedContent = useRef<string>("")

  const debouncedOnUpdate = useMemo(
    () =>
      onUpdate
        ? debounce((markdown: string) => onUpdate(markdown), 500)
        : undefined,
    [onUpdate]
  )

  // Outbound: Editor → caller (when user edits)
  useEffect(() => {
    if (!editor || !isEditing) return

    const handleUpdate = () => {
      if (isInternalUpdate.current) return
      onUserEdit?.()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const markdown = (editor.storage as any).markdown?.getMarkdown?.() ?? ""
      debouncedOnUpdate?.(markdown)
    }

    editor.on("update", handleUpdate)
    return () => {
      editor.off("update", handleUpdate)
    }
  }, [editor, isEditing, debouncedOnUpdate, onUserEdit])

  // Inbound: content prop → Editor (streaming or external update)
  useEffect(() => {
    if (!editor || !content) return
    if (content === lastSyncedContent.current) return

    isInternalUpdate.current = true
    editor.commands.setContent(content)
    lastSyncedContent.current = content
    isInternalUpdate.current = false
  }, [editor, content])

  /** Deliver a pending debounced edit now. */
  const flush = () => debouncedOnUpdate?.flush()
  return { isReady: !!editor, flush }
}
